import { Loader2, FileText, Download, Printer, Mail, UserCheck, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export const StatementTab = ({
  formData, statementDateFrom, setStatementDateFrom,
  statementDateTo, setStatementDateTo, showUnreconciledOnly,
  setShowUnreconciledOnly, includeCashSales, setIncludeCashSales,
  statementLoading, statementData,
  exportStatementCSV, handlePrintStatement, handleEmailStatement, handleEmailStatementToSelf,
  sendingEmail, formatDate,
  canCorrectLoyalty, onCorrectLoyalty, correctingRef,
}) => {
  const isLoyaltyCredit = (tx) =>
    tx.doc_type === 'CREDIT NOTE' && (tx.description || '').toLowerCase().includes('loyalty cashback') && !tx.reversed;
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-700 to-red-600 text-white p-4 rounded">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-6 w-6" />
          <h2 className="text-2xl font-bold">ACCOUNT STATEMENT</h2>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span>From</span>
              <Input
                type="date"
                value={statementDateFrom}
                onChange={(e) => setStatementDateFrom(e.target.value)}
                className="w-36 h-8 text-black"
              />
            </div>
            <div className="flex items-center gap-2">
              <span>To</span>
              <Input
                type="date"
                value={statementDateTo}
                onChange={(e) => setStatementDateTo(e.target.value)}
                className="w-36 h-8 text-black"
              />
            </div>
          </div>
          <div className="text-right">
            <p className="font-bold">{formData.account_number}</p>
            <p>{formData.name}</p>
            <p className="text-sm">{formData.contact_number}</p>
            {formData.whatsapp_number && <p className="text-sm text-green-600">WA: {formData.whatsapp_number}</p>}
            <p className="text-sm">{formData.email}</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={showUnreconciledOnly}
              onCheckedChange={setShowUnreconciledOnly}
              data-testid="statement-unreconciled-toggle"
            />
            <Label>Show Only Un-reconciled</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={includeCashSales}
              onCheckedChange={setIncludeCashSales}
              data-testid="statement-cash-sales-toggle"
            />
            <Label
              className="cursor-help"
              title="Cash / card sales are added as informational 'PAID AT TILL' lines that do NOT affect the balance."
            >
              Include cash / card sales
            </Label>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportStatementCSV}>
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrintStatement}>
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleEmailStatement}
            disabled={sendingEmail || !formData.email}
            className="border-blue-300 text-blue-600 hover:bg-blue-50"
            data-testid="statement-email-customer-btn"
          >
            {sendingEmail ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Mail className="h-4 w-4 mr-1" />
            )}
            Email Statement
          </Button>
          {handleEmailStatementToSelf && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEmailStatementToSelf}
              disabled={sendingEmail}
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
              title="Send a copy of this statement to the store's own email only (not the customer)"
              data-testid="statement-email-self-btn"
            >
              <UserCheck className="h-4 w-4 mr-1" />
              Email to Me
            </Button>
          )}
        </div>
      </div>

      {/* Statement Table */}
      <div className="border rounded overflow-auto max-h-[350px]">
        {statementLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 bg-gray-200">
              <TableRow>
                <TableHead className="font-bold">DocType</TableHead>
                <TableHead className="font-bold">InvNo</TableHead>
                <TableHead className="font-bold text-right">Debit</TableHead>
                <TableHead className="font-bold text-right">Credit</TableHead>
                <TableHead className="font-bold text-right">Balance</TableHead>
                <TableHead className="font-bold text-right">Paid</TableHead>
                <TableHead className="font-bold">Date</TableHead>
                <TableHead className="font-bold">User</TableHead>
                <TableHead className="font-bold">Description</TableHead>
                {canCorrectLoyalty && <TableHead className="font-bold text-center">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {statementData.transactions?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canCorrectLoyalty ? 10 : 9} className="text-center py-8 text-gray-500">
                    No transactions found for this period
                  </TableCell>
                </TableRow>
              ) : (
                statementData.transactions?.filter(tx => {
                  if (!showUnreconciledOnly) return true;
                  return tx.paid < tx.debit;
                }).map((tx, idx) => (
                  <TableRow key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 ${
                        tx.doc_type === 'PURCHASE' ? 'text-red-600' :
                        tx.doc_type === 'PAYMENT' ? 'text-green-600' :
                        tx.doc_type === 'CREDIT NOTE' ? 'text-green-600' :
                        'text-gray-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          tx.doc_type === 'PURCHASE' ? 'bg-red-500' :
                          tx.doc_type === 'PAYMENT' ? 'bg-green-500' :
                          tx.doc_type === 'CREDIT NOTE' ? 'bg-green-500' :
                          'bg-gray-500'
                        }`}></span>
                        {tx.doc_type}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono">{tx.inv_no}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">
                      {tx.debit > 0 ? tx.debit.toFixed(2) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      {tx.credit > 0 ? `-${tx.credit.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${tx.balance < 0 ? 'text-green-600' : ''}`}>
                      {tx.balance?.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {tx.paid > 0 ? tx.paid.toFixed(2) : '0.00'}
                    </TableCell>
                    <TableCell>{formatDate(tx.date)}</TableCell>
                    <TableCell>{tx.user}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={tx.description}>
                      {tx.description}
                    </TableCell>
                    {canCorrectLoyalty && (
                      <TableCell className="text-center">
                        {isLoyaltyCredit(tx) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onCorrectLoyalty(tx)}
                            disabled={correctingRef === tx.inv_no}
                            className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            title={`Correct the amount of ${tx.inv_no} (no reversal lines on the statement)`}
                            data-testid={`statement-tab-correct-loyalty-${tx.inv_no}`}
                          >
                            {correctingRef === tx.inv_no ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <><Pencil className="h-3 w-3 mr-1" /><span className="text-[11px] font-semibold">Correct</span></>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};
